import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Lock, Network } from 'lucide-react';
import { usePlanStream } from '@/hooks/usePlanStream';
import { blockerLines, needsWriteAccess, OUTCOME_TONE_STYLES, outcomeTone, statusLabel } from '@/lib/plans';
import { cn } from '@/lib/utils';
import { useBrowserStore } from '@/stores/browserStore';
import { usePlansStore } from '@/stores/plansStore';
import { useWriteEnableStore } from '@/stores/writeEnableStore';
import { BrowserPanel } from '@/components/browser/BrowserPanel';
import { StatusChip } from '@/components/changes/badges';
import { PlanBrief } from '@/components/changes/PlanBrief';
import { PlanChatBubble } from '@/components/changes/PlanChat';
import { PlanControls } from '@/components/changes/PlanControls';
import { PlanGraphDialog } from '@/components/changes/PlanGraphDialog';
import { AddedScopes } from '@/components/changes/AddedScopes';
import { BackToPlanning } from '@/components/changes/BackToPlanning';
import { RevertOutcome } from '@/components/changes/RevertOutcome';
import { RunTimeline } from '@/components/changes/RunTimeline';
import { StepList } from '@/components/changes/StepList';
import { StepPanel } from '@/components/changes/StepPanel';

const EDITABLE = new Set(['draft', 'ready', 'approved', 'scheduled']);

/**
 * One plan (D8): its brief, its steps (the graph a toggle away), its controls and its run, with its chat beside it.
 * With the browser panel open — the plan is being watched (D19), or a human step's "Take me there" walked it — the
 * browser takes the right half instead.
 */
export function PlanPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { plansById, draft, loadPlan, editTitle, error, clearError, busy } = usePlansStore();
  const setPendingPlan = useWriteEnableStore((s) => s.setPendingPlan);
  const panelOpen = useBrowserStore((s) => s.panelOpen);
  const closePanel = useBrowserStore((s) => s.closePanel);
  const plan = plansById[id];
  const [graphOpen, setGraphOpen] = useState(false);
  // Beside the plan on a wide screen; below that it would cover the plan, so it waits to be opened.
  usePlanStream(id);

  useEffect(() => {
    void loadPlan(id);
  }, [id, loadPlan]);

  if (!plan) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        {busy.load ? <><Loader2 className="h-4 w-4 animate-spin" /> Loading the plan…</> : (error ?? 'Plan not found')}
      </div>
    );
  }

  const editable = EDITABLE.has(plan.status);
  const stale = draft !== null && draft.planId === plan.id && draft.stale;
  const title = draft !== null && draft.planId === plan.id ? draft.title : plan.title;
  const tone = outcomeTone(plan.status);
  // The scopes blocker has its own way in only once write access is on; before that, the controls lead with enabling it (D3, D7).
  const offerScopes = plan.missing_scopes.length > 0 && !needsWriteAccess(plan);

  const page = (
    <div className="mx-auto flex h-full max-w-7xl flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-col gap-3">
        <div className="min-w-0">
          <Link to="/changes" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowLeft className="h-3 w-3" /> Changes</Link>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            {editable ? (
              <input value={title} onChange={(e) => editTitle(e.target.value)} aria-label="Plan title" className="min-w-[12rem] flex-1 bg-transparent text-xl font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 rounded px-1 -mx-1" />
            ) : (
              <h1 className="min-w-0 flex-1 break-words text-xl font-semibold text-foreground">{plan.title}</h1>
            )}
            <StatusChip status={plan.status} label={statusLabel(plan)} />
            <span className="text-xs text-muted-foreground">v{plan.version}</span>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Drafted by {plan.created_by.email}
            {plan.approved_by && <> · approved by {plan.approved_by.email}</>}
            {plan.status === 'scheduled' && plan.scheduled_at && <> · scheduled for {new Date(plan.scheduled_at).toLocaleString()}</>}
            {plan.reverts_plan_id && <> · reverts <Link to={`/changes/${plan.reverts_plan_id}`} className="underline hover:text-foreground">an earlier plan</Link></>}
          </p>
        </div>
        <PlanControls plan={plan} />
      </div>

      {error && (
        <div className="flex items-center justify-between rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          <span>{error}</span>
          <button onClick={clearError} className="text-xs underline">dismiss</button>
        </div>
      )}
      {stale && (
        <div className="flex items-center justify-between rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
          <span>Someone saved a newer version of this plan while you were editing.</span>
          <button onClick={() => void loadPlan(id)} className="text-xs underline">reload</button>
        </div>
      )}
      {plan.outcome_line && tone !== null && (
        <div className={cn('rounded-lg border px-3 py-2 text-sm', OUTCOME_TONE_STYLES[tone])}>
          {plan.outcome_line}
          {plan.reverted_by_plan_id && <> Since then it was <Link to={`/changes/${plan.reverted_by_plan_id}`} className="underline">reverted</Link>.</>}
        </div>
      )}
      {!plan.readiness.ok && plan.status === 'draft' && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
          <span className="font-medium">Before approving:</span>
          <ul className="mt-1 list-disc pl-5">{blockerLines(plan).map((line, i) => <li key={i}>{line}</li>)}</ul>
          {offerScopes && (
            <button
              onClick={() => {
                setPendingPlan({ planId: plan.id, scopes: plan.missing_scopes });
                navigate('/chat?setup=write');
              }}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Lock className="h-3.5 w-3.5" /> Add the {plan.missing_scopes.length === 1 ? 'scope' : `${plan.missing_scopes.length} scopes`} to your Zoom app
            </button>
          )}
        </div>
      )}

      <BackToPlanning plan={plan} />
      <PlanBrief plan={plan} />

      <section className="min-w-0 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-foreground">Steps <span className="font-normal text-muted-foreground">({plan.steps.length})</span></h2>
          <button onClick={() => setGraphOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground">
            <Network className="h-3.5 w-3.5" /> View as graph
          </button>
        </div>
        <StepList steps={plan.steps} detail={(step) => <StepPanel plan={plan} step={step} editable={editable} />} />
        <p className="text-xs text-muted-foreground">Select a step to see what it does, what it needs, and what it did.</p>
      </section>
      {graphOpen && <PlanGraphDialog plan={plan} editable={editable} onClose={() => setGraphOpen(false)} />}

      {plan.revert && <RevertOutcome plan={plan} />}
      <AddedScopes plan={plan} />
      <RunTimeline plan={plan} />
    </div>
  );

  return (
    <div className="flex h-full">
      <div className="min-w-0 flex-1 overflow-y-auto">{page}</div>
      {panelOpen && (
        <div className="hidden w-[46%] min-w-[480px] border-l border-border lg:block">
          <BrowserPanel onClose={closePanel} />
        </div>
      )}
      <PlanChatBubble plan={plan} />
    </div>
  );
}
